import { getToken, onMessage, isSupported } from 'firebase/messaging'
import { doc, addDoc, collection, serverTimestamp, setDoc, query, where, getDocs, getDoc } from 'firebase/firestore'
import { db, getMessagingInstance, VAPID_KEY } from './firebase'
import toast from 'react-hot-toast'

class NotificationService {
  constructor() {
    this.messaging = null
    this.currentToken = null
    this.isSupported = false
    this.initialized = false
  }

  // Initialize FCM messaging with retry logic for page refreshes
  async initialize() {
    // Reset state on initialization to handle page refreshes
    this.messaging = null
    this.currentToken = null
    this.isSupported = false
    this.initialized = false

    try {
      
      // Wait a bit for the page to fully load after refresh
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Check if FCM is supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('❌ Push notifications not supported in this browser')
        this.initialized = true // Mark as initialized but not supported
        return true // Return true to allow app to continue with browser notifications
      }

      // Check if Firebase messaging is available
      
      if (!getToken || !onMessage) {
        console.log('❌ Firebase messaging not available')
        this.initialized = true // Mark as initialized but not supported
        return true // Return true to allow app to continue with browser notifications
      }

      // Ensure service worker is properly registered with retry logic
      let registration = null
      let retryCount = 0
      const maxRetries = 3
      
      while (!registration && retryCount < maxRetries) {
        try {
          registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
          if (registration) {
            // Check if service worker is active
            if (registration.active) {
            } else {
              console.log('⚠️ Service worker is not active, waiting...')
              await new Promise(resolve => setTimeout(resolve, 1000))
              registration = null
            }
          } else {
            console.log('⚠️ Service worker not found, trying to register...')
            try {
              registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
              // Wait for service worker to be ready
              await navigator.serviceWorker.ready
            } catch (swError) {
              retryCount++
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 2000))
              }
            }
          }
        } catch (error) {
          console.log('⚠️ Error checking service worker registration:', error)
          retryCount++
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        }
      }
      
      if (!registration) {
        console.log('❌ Could not register service worker after retries')
        this.initialized = true // Mark as initialized but not supported
        return true // Return true to allow app to continue with browser notifications
      }

      // Get the messaging instance with retry logic
      let messagingRetryCount = 0
      const maxMessagingRetries = 3
      
      while (!this.messaging && messagingRetryCount < maxMessagingRetries) {
        try {
          this.messaging = await getMessagingInstance()
          
          if (!this.messaging) {
            throw new Error('Messaging instance is null')
          }
        } catch (error) {
          messagingRetryCount++
          if (messagingRetryCount < maxMessagingRetries) {
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        }
      }
      
      if (!this.messaging) {
        
        // Even if FCM fails, we can still use browser notifications
        this.initialized = true
        this.messaging = null
        this.isSupported = false
        return true // Return true so the app continues to work
      }
      console.log('✅ Got messaging instance')

      // Now check if messaging is supported in this environment
      console.log('🔍 Checking if messaging is supported...')
      try {
        const messagingSupported = await isSupported()
        if (!messagingSupported) {
          console.log('❌ Firebase messaging not supported in this environment')
          this.initialized = true // Mark as initialized but not supported
          return true // Return true to allow app to continue with browser notifications
        }
        console.log('✅ Messaging is supported')
      } catch (error) {
        console.log('⚠️ Could not check messaging support, continuing anyway:', error)
      }

      this.isSupported = true
      this.initialized = true

      // Request permission
      console.log('🔧 Requesting notification permission...')
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        await this.getToken()
        this.setupMessageListener()
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error('❌ Error initializing notifications:', error)
      console.error('❌ Error details:', error.message)
      console.error('❌ Error stack:', error.stack)
      
      // Try fallback initialization
      console.log('🔄 Attempting fallback initialization...')
      try {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
          this.initialized = true
          this.isSupported = false // Mark as not FCM supported but basic notifications work
          return true
        } else {
          this.initialized = true
          return true // Return true to allow app to continue even without notifications
        }
      } catch (fallbackError) {
        console.error('❌ Fallback initialization failed:', fallbackError)
        this.initialized = true // Mark as initialized but not supported
        return true // Return true to allow app to continue even if notifications fail
      }
    }
  }

  // Get FCM token
  async getToken() {
    try {
      
      if (!this.messaging) {
        console.log('❌ getToken: Messaging not initialized')
        return null
      }

      
      // Get the registration token
      this.currentToken = await getToken(this.messaging, {
        vapidKey: VAPID_KEY
      })

      if (this.currentToken) {
        console.log('✅ getToken: FCM Token obtained:', this.currentToken.substring(0, 50) + '...')
        
        // Save token to user's profile in Firestore
        await this.saveTokenToUser(this.currentToken)
        
        return this.currentToken
      } else {
        console.log('❌ getToken: No registration token available')
        return null
      }
    } catch (error) {
      console.error('❌ getToken: Error getting FCM token:', error)
      console.error('❌ getToken: Error details:', error.message)
      console.error('❌ getToken: Error code:', error.code)
      console.error('❌ getToken: Error stack:', error.stack)
      return null
    }
  }

  // Save FCM token to user's profile
  async saveTokenToUser(token) {
    try {
      // Get current user from auth
      const { getAuth } = await import('firebase/auth')
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore')
      const { db } = await import('./firebase')
      
      const auth = getAuth()
      const currentUser = auth.currentUser
      
      if (!currentUser) {
        console.log('❌ No authenticated user to save FCM token')
        return
      }
      
      console.log('💾 Saving FCM token to user profile:', token)
      console.log('💾 User ID:', currentUser.uid)
      
      // Update user document with FCM token
      const userRef = doc(db, 'users', currentUser.uid)
      await updateDoc(userRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: serverTimestamp()
      })
      
    } catch (error) {
      console.error('❌ Error saving FCM token:', error)
    }
  }

  // Setup message listener for foreground notifications
  setupMessageListener() {
    if (!this.messaging) return

    onMessage(this.messaging, (payload) => {
      console.log('📱 Message received in foreground:', payload)
      
      // Show notification even when app is in foreground
      this.showLocalNotification(payload)
    })
  }

  // Show local notification with proper permission handling
  async showLocalNotification(payload) {
    const { title, body, icon } = payload.notification || {}
    
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.log('⚠️ This browser does not support notifications')
        return
      }

      // Check if we have permission
      if (Notification.permission === 'default') {
        // Request permission if not granted yet
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') {
          return
        }
      } else if (Notification.permission !== 'granted') {
        return
      }

      // Now show the notification
      if ('serviceWorker' in navigator && 'showNotification' in ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready.then(registration => {
          registration.showNotification(title || 'New Alert', {
            body: body || 'You have a new emergency alert',
            icon: icon || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjMzM2N0Y3Ii8+Cjwvc3ZnPgo=',
            badge: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjMzM2N0Y3Ii8+Cjwvc3ZnPgo=',
            tag: 'alert-notification',
            requireInteraction: true,
            actions: [
              {
                action: 'view',
                title: 'View Alert'
              },
              {
                action: 'dismiss',
                title: 'Dismiss'
              }
            ]
          })
        })
      } else {
        // Fallback to native notifications if service worker not available
        new Notification(title || 'New Alert', {
          body: body || 'You have a new emergency alert',
          icon: icon || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkyIiBoZWlnaHQ9IjE5MiIgdmlld0JveD0iMCAwIDE5MiAxOTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxOTIiIGhlaWdodD0iMTkyIiByeD0iMjQiIGZpbGw9IiM2MzY2RjEiLz4KPHRleHQgeD0iOTYiIHk9IjExMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkM8L3RleHQ+Cjwvc3ZnPgo='
        })
      }
    } catch (error) {
      console.error('❌ Error showing notification:', error)
    }
  }

  // Send notification to platform admin when organization request is submitted
  async sendOrganizationRequestNotification(requestData) {
    try {
      
      // Create a notification record in Firestore
      const notificationData = {
        type: 'organization_request_submitted',
        title: 'New Organization Request',
        message: `${requestData.organizationName} has submitted a request to create an organization`,
        organizationName: requestData.organizationName,
        adminName: `${requestData.adminFirstName} ${requestData.adminLastName}`,
        adminEmail: requestData.adminEmail,
        requestId: requestData.id || 'pending',
        createdAt: serverTimestamp(),
        sent: true,
        targetUser: 'jed@chimeo.app' // Only notify platform admin
      }

      // Add to platform admin's notifications subcollection
      const notificationId = `org_request_${Date.now()}`
      const notificationPath = `users/jed@chimeo.app/notifications/${notificationId}`
      console.log('🔍 Saving notification to path:', notificationPath)
      
      await setDoc(doc(db, 'users', 'jed@chimeo.app', 'notifications', notificationId), notificationData)
      console.log('✅ Organization request notification record saved to Firestore under platform admin')

      // Always show notification using fallback method since FCM is having issues
      console.log('🔔 Showing notification using fallback method...')
      await this.showFallbackNotification(
        'New Organization Request',
        `${requestData.organizationName} - ${requestData.adminFirstName} ${requestData.adminLastName}`,
        'organization_request'
      )

      
    } catch (error) {
      console.error('❌ Error sending organization request notification:', error)
    }
  }

  // Send notification to users when alert is created
  async sendAlertNotification(alertData) {
    try {
      
      // Always show browser notification as primary method
      this.showBrowserNotification(alertData.title, alertData.message)
      
      // Get all users who should receive this alert
      const targetUsers = await this.getAlertTargetUsers(alertData)
      console.log(`🔔 Found ${targetUsers.length} users to notify for alert: ${alertData.title}`)
      
      // Send notifications to all target users
      const notificationPromises = targetUsers.map(user => this.sendNotificationToUser(user, alertData))
      await Promise.all(notificationPromises)
      
      toast.success('Alert posted and notifications sent!')
      
    } catch (error) {
      console.error('❌ Error sending alert notification:', error)
      toast.error('Alert posted but notification failed')
    }
  }

  // Get all users who should receive this alert
  async getAlertTargetUsers(alertData) {
    try {
      console.log('🔍 getAlertTargetUsers: Starting to find target users for alert:', alertData.title)
      console.log('🔍 getAlertTargetUsers: Alert data:', {
        organizationId: alertData.organizationId,
        groupId: alertData.groupId,
        organizationName: alertData.organizationName
      })
      
      const targetUsers = []
      
      // If it's a group-specific alert, get group followers
      if (alertData.groupId) {
        console.log(`🔍 Getting group followers for group: ${alertData.groupId}`)
        const groupFollowers = await this.getGroupFollowers(alertData.organizationId, alertData.groupId)
        console.log(`🔍 Group followers found: ${groupFollowers.length}`)
        targetUsers.push(...groupFollowers)
      } else {
        // If it's an organization-wide alert, get organization followers
        console.log(`🔍 Getting organization followers for org: ${alertData.organizationId}`)
        const orgFollowers = await this.getOrganizationFollowers(alertData.organizationId)
        console.log(`🔍 Organization followers found: ${orgFollowers.length}`)
        targetUsers.push(...orgFollowers)
      }
      
      // Remove duplicates based on userId
      const uniqueUsers = targetUsers.filter((user, index, self) => 
        index === self.findIndex(u => u.userId === user.userId)
      )
      
      console.log(`🔍 Found ${uniqueUsers.length} unique users to notify`)
      console.log('🔍 Target users:', uniqueUsers.map(u => ({ email: u.email, userId: u.userId, hasFCM: !!u.fcmToken })))
      return uniqueUsers
    } catch (error) {
      console.error('❌ Error getting alert target users:', error)
      return []
    }
  }

  // Get users who follow a specific group
  async getGroupFollowers(organizationId, groupId) {
    try {
      console.log(`🔍 Getting group followers for groupId: ${groupId} in org: ${organizationId}`)
      
      // First, get the group data to find the group name
      const groupRef = doc(db, 'organizations', organizationId, 'groups', groupId)
      const groupDoc = await getDoc(groupRef)
      
      if (!groupDoc.exists()) {
        console.log('❌ Group not found:', groupId)
        return []
      }
      
      const groupData = groupDoc.data()
      const groupName = groupData.name
      console.log(`🔍 Group name: ${groupName}`)
      
      const usersQuery = query(collection(db, 'users'))
      const usersSnapshot = await getDocs(usersQuery)
      
      console.log(`🔍 Checking ${usersSnapshot.size} users for group following`)
      
      const groupFollowers = []
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data()
        
        // Debug: Log user's following status
        console.log(`🔍 Checking user: ${userData.email}`)
        console.log(`  - groupPreferences:`, userData.groupPreferences)
        console.log(`  - followedGroups:`, userData.followedGroups)
        console.log(`  - Looking for group: ${groupName} (ID: ${groupId})`)
        
        // Check if user follows this group via groupPreferences (using group name as key)
        if (userData.groupPreferences && userData.groupPreferences[groupName] === true) {
          groupFollowers.push({
            userId: userDoc.id,
            email: userData.email,
            displayName: userData.displayName,
            fcmToken: userData.fcmToken
          })
          console.log(`✅ Found group follower: ${userData.email} (via groupPreferences)`)
        }
        // Also check old followedGroups structure for backward compatibility (using group ID)
        else if (userData.followedGroups && userData.followedGroups.includes(groupId)) {
          groupFollowers.push({
            userId: userDoc.id,
            email: userData.email,
            displayName: userData.displayName,
            fcmToken: userData.fcmToken
          })
          console.log(`✅ Found group follower: ${userData.email} (via followedGroups)`)
        } else {
          console.log(`❌ User ${userData.email} does not follow this group`)
        }
      }
      
      console.log(`🔍 Found ${groupFollowers.length} group followers for group: ${groupName}`)
      return groupFollowers
    } catch (error) {
      console.error('❌ Error getting group followers:', error)
      return []
    }
  }

  // Get users who follow a specific organization
  async getOrganizationFollowers(organizationId) {
    try {
      console.log(`🔍 Getting organization followers for org: ${organizationId}`)
      
      const followersQuery = query(
        collection(db, 'organizations', organizationId, 'followers')
      )
      const followersSnapshot = await getDocs(followersQuery)
      
      console.log(`🔍 Found ${followersSnapshot.size} followers in organization subcollection`)
      
      const orgFollowers = []
      
      for (const followerDoc of followersSnapshot.docs) {
        const followerData = followerDoc.data()
        console.log(`🔍 Follower data:`, followerData)
        
        // Get user data to get FCM token
        const userDoc = await getDoc(doc(db, 'users', followerData.userId))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          console.log(`✅ Found organization follower: ${userData.email}`)
          orgFollowers.push({
            userId: followerData.userId,
            email: userData.email,
            displayName: userData.displayName,
            fcmToken: userData.fcmToken
          })
        } else {
          console.log(`❌ User document not found for follower: ${followerData.userId}`)
        }
      }
      
      console.log(`🔍 Found ${orgFollowers.length} organization followers`)
      return orgFollowers
    } catch (error) {
      console.error('❌ Error getting organization followers:', error)
      return []
    }
  }

  // Send notification to a specific user
  async sendNotificationToUser(user, alertData) {
    try {
      console.log(`🔔 Sending notification to user: ${user.email}`)
      
      // Create a notification record in Firestore
      const notificationData = {
        type: 'alert_created',
        title: alertData.title,
        message: alertData.message,
        alertId: alertData.id,
        organizationId: alertData.organizationId,
        organizationName: alertData.organizationName,
        createdAt: serverTimestamp(),
        sent: true,
        targetUser: user.email
      }

      // Add to user's notifications subcollection
      const notificationId = `alert_${Date.now()}_${user.userId}`
      await setDoc(doc(db, 'users', user.userId, 'notifications', notificationId), notificationData)
      console.log(`✅ Alert notification record saved for user: ${user.email}`)

      // Try to send FCM push notification if token exists
      if (user.fcmToken) {
        try {
          await this.sendFCMPushNotification(user.fcmToken, {
            title: alertData.title,
            body: alertData.message,
            data: {
              type: 'alert',
              alertId: alertData.id,
              organizationId: alertData.organizationId
            }
          })
          console.log(`✅ FCM push notification sent to: ${user.email}`)
        } catch (fcmError) {
          console.warn(`⚠️ FCM push failed for ${user.email}, using fallback:`, fcmError.message)
          // Fallback to browser notification
          this.showBrowserNotification(alertData.title, alertData.message)
        }
      } else {
        console.log(`⚠️ No FCM token for user: ${user.email}`)
        // Show browser notification as fallback
        this.showBrowserNotification(alertData.title, alertData.message)
      }
      
    } catch (error) {
      console.error(`❌ Error sending notification to user ${user.email}:`, error)
    }
  }

  // Show browser notification as fallback
  showBrowserNotification(title, body) {
    try {
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          const notification = new Notification(title, {
            body: body,
            icon: '/favicon.svg',
            tag: `chimeo-alert-${Date.now()}`,
            requireInteraction: true
          })
          
          // Handle notification click
          notification.onclick = () => {
            window.focus()
            notification.close()
          }
          
          // Auto-close after 15 seconds
          setTimeout(() => {
            notification.close()
          }, 15000)
          
          console.log('✅ Browser notification shown as fallback')
        } else if (Notification.permission === 'default') {
          console.log('🔔 Requesting notification permission...')
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              this.showBrowserNotification(title, body)
            } else {
            }
          })
        } else {
          console.log('⚠️ Browser notifications permission denied')
        }
      } else {
        console.log('⚠️ Browser notifications not supported')
      }
    } catch (error) {
      console.error('❌ Error showing browser notification:', error)
    }
  }

  // Send FCM push notification via Firebase Function
  async sendFCMPushNotification(token, payload) {
    try {
      console.log('🔔 FCM notification payload:', payload)
      
      // Try to use Firebase Function for FCM
      try {
        const { httpsCallable } = await import('firebase/functions')
        const { functions } = await import('./firebase')
        
        if (functions) {
          const sendPushNotification = httpsCallable(functions, 'sendPushNotification')
          
          // Use placeholder user ID like the working version
          const userId = 'placeholder-user-id'
          
          const result = await sendPushNotification({
            userId,
            title: payload.title,
            body: payload.body,
            data: payload.data
          })
          
          console.log('✅ FCM push notification sent via Firebase Function:', result.data)
          return true
        } else {
          throw new Error('Firebase Functions not available')
        }
      } catch (functionError) {
        console.warn('⚠️ Firebase Function failed, using browser notification fallback:', functionError.message)
        
        // Fallback to browser notifications
        if ('Notification' in window) {
          if (Notification.permission === 'granted') {
            const notification = new Notification(payload.title, {
              body: payload.body,
              icon: '/favicon.svg',
              tag: `chimeo-alert-${Date.now()}`,
              data: payload.data,
              requireInteraction: true
            })
            
            // Handle notification click
            notification.onclick = () => {
              window.focus()
              notification.close()
            }
            
            // Auto-close after 15 seconds
            setTimeout(() => {
              notification.close()
            }, 15000)
            
            console.log('✅ Browser notification shown for FCM fallback')
          } else if (Notification.permission === 'default') {
            console.log('🔔 Requesting notification permission...')
            const permission = await Notification.requestPermission()
            if (permission === 'granted') {
              // Retry showing notification
              return this.sendFCMPushNotification(token, payload)
            } else {
            }
          } else {
            console.log('⚠️ Browser notifications permission denied')
          }
        } else {
          console.log('⚠️ Browser notifications not supported')
        }
      }
      
      return true
    } catch (error) {
      console.error('❌ Error sending FCM push notification:', error)
      throw error
    }
  }

  // Robust fallback notification method that works without FCM
  async showFallbackNotification(title, body, type = 'general', icon = null) {
    try {
      
      if (!('Notification' in window)) {
        console.log('❌ Browser notifications not supported')
        return false
      }

      // Request permission if not granted
      if (Notification.permission === 'default') {
        console.log('🔔 Requesting notification permission...')
        const permission = await Notification.requestPermission()
        console.log('🔔 Permission result:', permission)
      }

      if (Notification.permission === 'granted') {
        const notificationOptions = {
          body: body,
          icon: icon || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkyIiBoZWlnaHQ9IjE5MiIgdmlld0JveD0iMCAwIDE5MiAxOTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxOTIiIGhlaWdodD0iMTkyIiByeD0iMjQiIGZpbGw9IiM2MzY2RjEiLz4KPHRleHQgeD0iOTYiIHk9IjExMCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjI0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkM8L3RleHQ+Cjwvc3ZnPgo=',
          badge: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjMzM2N0Y3Ii8+Cjwvc3ZnPgo=',
          tag: `chimeo-${type}-${Date.now()}`,
          requireInteraction: true,
          data: {
            type: type,
            timestamp: Date.now(),
            source: 'chimeo-web'
          }
        }

        // Add actions based on notification type
        if (type === 'organization_request') {
          notificationOptions.actions = [
            {
              action: 'view',
              title: 'View Request'
            },
            {
              action: 'dismiss',
              title: 'Dismiss'
            }
          ]
        } else if (type === 'alert') {
          notificationOptions.actions = [
            {
              action: 'view',
              title: 'View Alert'
            },
            {
              action: 'dismiss',
              title: 'Dismiss'
            }
          ]
        }

        const notification = new Notification(title, notificationOptions)
        
        // Add event listeners
        notification.onclick = () => {
          notification.close()
          
          // Focus the window
          if (window.focus) {
            window.focus()
          }
          
          // You can add navigation logic here based on type
          if (type === 'organization_request') {
            // Navigate to organization requests page
            window.location.href = '/org-requests'
          } else if (type === 'alert') {
            // Navigate to alerts page
            window.location.href = '/alerts'
          }
        }

        notification.onclose = () => {
        }

        return true
      } else {
        return false
      }
    } catch (error) {
      console.error('❌ Error showing fallback notification:', error)
      return false
    }
  }

  // Simple fallback notification method that doesn't require Firebase Messaging
  async showSimpleNotification(title, body, icon = null) {
    return await this.showFallbackNotification(title, body, 'general', icon)
  }

  // Request notification permission
  async requestPermission() {
    try {
      console.log('🔔 Requesting notification permission...')
      const permission = await Notification.requestPermission()
      console.log('🔔 Permission result:', permission)
      
      if (permission === 'granted') {
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error)
      return false
    }
  }

  // Trigger cloud function to send push notifications (commented out for now)
  // async triggerCloudFunction(alertData) {
  //   try {
  //     // This will call your Firebase Cloud Function
  //     // The function will send push notifications to all subscribed users
  //     const response = await fetch('/api/sendAlertNotifications', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(alertData)
  //     })

  //     if (!response.ok) {
  //       throw new Error('Failed to trigger notification service')
  //     }

  //     return await response.json()
  //   } catch (error) {
  //     console.error('❌ Error triggering cloud function:', error)
  //     throw error
  //   }
  // }

  // Get current token
  getCurrentToken() {
    return this.currentToken
  }

  // Check if notifications are supported
  isNotificationsSupported() {
    return this.isSupported || (this.initialized && 'Notification' in window && Notification.permission === 'granted')
  }

  // Check if service is initialized
  isInitialized() {
    return this.initialized
  }

  // Reinitialize the service (useful after page refresh)
  async reinitialize() {
    console.log('🔄 Reinitializing notification service...')
    this.messaging = null
    this.currentToken = null
    this.isSupported = false
    this.initialized = false
    return await this.initialize()
  }

  // Check if notifications are working properly
  async checkNotificationHealth() {
    try {
      console.log('🔍 Checking notification service health...')
      
      // Check if service worker is registered and active
      const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
      if (!registration || !registration.active) {
        console.log('❌ Service worker not properly registered or active')
        return false
      }
      
      // Check if messaging instance is available
      if (!this.messaging) {
        console.log('❌ Messaging instance not available')
        return false
      }
      
      // Check if we have a valid token
      if (!this.currentToken) {
        console.log('❌ No FCM token available')
        return false
      }
      
      // Check notification permission
      if (Notification.permission !== 'granted') {
        return false
      }
      
      return true
    } catch (error) {
      console.error('❌ Error checking notification health:', error)
      return false
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService()
export default notificationService
